import type { RewardedAdCompletion, RewardedAdStart } from '@/services/rewardedAds';

/**
 * Последовательность одного показа рекламы.
 *
 * Вынесена из компонента намеренно: правило «прерванный показ не
 * превращается в награду» — часть бизнес-логики, а не разметки. Здесь же
 * видно, что клиент не решает ничего сам: старт разрешает сервер,
 * завершение он же верифицирует у провайдера.
 */
export interface RewardedAdPlayback {
  /** Досмотрел ли пользователь показ до конца. */
  completed: boolean;
  /** Идентификатор показа у рекламной сети, если она его выдала. */
  providerEventId: string | null;
}

export interface RunRewardedAdDeps {
  start: () => Promise<RewardedAdStart>;
  play: (start: RewardedAdStart) => Promise<RewardedAdPlayback>;
  complete: (input: {
    eventId: string;
    providerEventId: string | null;
  }) => Promise<RewardedAdCompletion>;
}

export type RewardedAdRun =
  | { outcome: 'rewarded'; completion: RewardedAdCompletion }
  | { outcome: 'abandoned' };

export async function runRewardedAd(deps: RunRewardedAdDeps): Promise<RewardedAdRun> {
  // Отказ сервера (лимит, нет сессии) — это исключение вызывающего кода:
  // показывать рекламу, за которую награды не будет, бессмысленно.
  const started = await deps.start();

  const playback = await deps.play(started);

  if (!playback.completed) {
    return { outcome: 'abandoned' };
  }

  const completion = await deps.complete({
    eventId: started.eventId,
    providerEventId: playback.providerEventId,
  });

  return { outcome: 'rewarded', completion };
}
